import { CommonModule, DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  model,
  output,
  resource,
  SecurityContext,
  signal,
  untracked,
  viewChild,
  viewChildren,
  ViewEncapsulation,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ClarityModule, ClrForm, ClrLoadingState } from '@clr/angular';
import { ToastrService } from 'ngx-toastr';
import { DeselectSingleSelectedDirective } from 'src/app/directives/deselect-single-selected.directive';

import { HideIDColumnsPipe } from 'src/app/pipes/hide-idcolumns.pipe';
import { MrTranslatePipe } from 'src/app/pipes/mr-translate.pipe';
import { TablePrettyPrintPipe } from 'src/app/pipes/tablePrettyPrint.pipe';
import { APIService } from 'src/app/services/APIService/api.service';
import { BaseDataRoute, BaseItemsHandler } from '../baseitems-wrapper.component';
import { TABLE_EMPTY } from 'src/app/models/ui/table/table';
import { BaseItem } from '../baseitems-wrapper.component';
import { PreviewThumbnailsComponent } from '../../../_shared/preview-thumbnails/preview-thumbnails.component';
import { ConfirmService } from 'src/app/services/confirm.service';
import { DomSanitizer } from '@angular/platform-browser';
import { FormGroupOfType } from 'src/app/types/shared';
import { errorHandle } from 'src/app/misc/functions';

@Component({
  selector: 'app-baseitems-sbauw-modal',
  templateUrl: './baseitems-sbauw-modal.component.html',
  styleUrl: './baseitems-sbauw-modal.component.scss',
  imports: [
    CommonModule,
    ClarityModule,
    FormsModule,
    ReactiveFormsModule,
    DeselectSingleSelectedDirective,
    PreviewThumbnailsComponent,
    HideIDColumnsPipe,
    MrTranslatePipe,
    TablePrettyPrintPipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class Baseitems_SBAUW_ModalComponent implements BaseItemsHandler {
  private readonly apiService = inject(APIService);
  private readonly mrTranslate = inject(MrTranslatePipe);
  private readonly toastr = inject(ToastrService);
  private readonly confirm = inject(ConfirmService);
  private readonly domSanitizer = inject(DomSanitizer);
  private readonly doc = inject(DOCUMENT);

  readonly refresh = output<BaseItem[]>();
  readonly isModalOpen = model<boolean>();

  protected readonly isRep = this.apiService.isRep;

  readonly route = model.required<BaseDataRoute>();
  readonly preSelected = computed(() => this.route().selected);

  readonly anweisung = viewChild<PreviewThumbnailsComponent>('anweisung');
  readonly clrForms = viewChildren(ClrForm);

  protected readonly selectedRow = signal<SBAUW_Item | null>(null);

  private readonly tableResource = resource({
    params: () => this.isModalOpen() || undefined,
    loader: () => this.apiService.getBaseItems('SBAUW'),
    defaultValue: TABLE_EMPTY(),
  });
  protected readonly table = this.tableResource.value;
  protected readonly isLoading = this.tableResource.isLoading;
  protected readonly loadState = signal(ClrLoadingState.DEFAULT);
  private isChanged = false;

  protected readonly form = new FormGroupOfType<Pick<SBAUW_Item, 'Bezeichnung' | 'Prüfung'>>({
    Bezeichnung: new FormControl('', Validators.required),
    Prüfung: new FormControl(false, { nonNullable: true }),
  });

  constructor() {
    effect(() => this.form.reset(this.selectedRow() ?? undefined));

    effect(() => {
      const preSelect = this.preSelected();
      if (preSelect && this.table().columns.length)
        untracked(() => {
          this.route.update(route => ({ ...route, selected: null }));
          switch (typeof preSelect) {
            case 'string': {
              const found = this.table().rows.find(row => row.Bezeichnung == preSelect);
              if (found) this.selectedRow.set(found);
              else this.form.reset({ Bezeichnung: preSelect });
              break;
            }
            case 'number': {
              this.selectedRow.set({ ID: preSelect } as SBAUW_Item);
              break;
            }
          }
        });
    });

    effect(() => {
      if (!this.anweisung()) return;
      const base64 = this.selectedRow()?.Anweisung;

      untracked(() => {
        this.anweisung().unpackFiles(base64 && [{ base64, name: 'Anweisung.pdf' }]);
      });
    });
    effect(() => {
      const status = this.tableResource.status();
      untracked(() => {
        if (status == 'reloading') {
          this.isChanged = true;
        } else if (status == 'resolved' && this.isChanged) {
          this.isChanged = false;
          this.refresh.emit(this.table().rows);
        }
      });
    });
  }

  protected async onSubmit() {
    if (this.form.invalid) {
      this.clrForms().forEach(form => form.markAsTouched());
      return;
    }

    this.loadState.set(ClrLoadingState.LOADING);
    try {
      const anweisung = (await this.anweisung().packUnsavedFiles())[0];

      const sendObj: SBAUW_Item = {
        ID: this.selectedRow()?.ID,
        ...this.form.getRawValue(),
        Anweisung: anweisung?.base64,
      };

      const id = await this.apiService.setBaseItems('SBAUW', sendObj);

      this.loadState.set(ClrLoadingState.SUCCESS);
      this.toastr.success(this.mrTranslate.transform('Erfolgreich gespeichert'));
      this.tableResource.reload();
      this.selectedRow.set({ ID: id } as SBAUW_Item);
    } catch (er) {
      this.loadState.set(ClrLoadingState.DEFAULT);
      this.toastr.error(errorHandle(er), this.mrTranslate.transform('Etwas ist schief gelaufen'));
    }
  }

  protected async deleteRow() {
    try {
      await this.apiService.deleteBaseItems('SBAUW', this.selectedRow().ID);

      this.toastr.success(this.mrTranslate.transform('Eintrag wurde gelöscht'));
      this.tableResource.reload();
      this.selectedRow.set(null);
    } catch (er) {
      this.toastr.error(errorHandle(er), this.mrTranslate.transform('Etwas ist schief gelaufen'));
    }
  }

  protected showPDF(file: File) {
    const anchor = this.doc.createElement('a');
    anchor.href = this.domSanitizer.sanitize(SecurityContext.URL, URL.createObjectURL(file));
    anchor.target = 'blank';
    anchor.click();
    anchor.remove();
  }

  protected async close() {
    const ok = () =>
      this.confirm.warning(`
      ${this.mrTranslate.transform('Sie haben ungespeicherte Änderungen')}.
      ${this.mrTranslate.transform('Trotzdedm schliessen')}?
    `);

    if ((this.form.pristine && !this.anweisung().isChanged()) || (await ok())) this.isModalOpen.set(false);
  }
}

export interface SBAUW_Item extends BaseItem {
  Prüfung: boolean;
  Anweisung?: string;
}
